import { Component, Input, signal, computed, inject, ViewChild } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { ReactiveFormsModule, FormControl, FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { trigger, transition, style, animate, keyframes, state, query } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { EffortEditorComponent } from '../dialogs/effort-editor/effort-editor.component';
import { Effort } from '../models/effort';
import { Observable, startWith, map, combineLatest, filter, firstValueFrom, first, Subscription, Subject, debounceTime, BehaviorSubject, distinct } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Data, Params, Router, RouterModule } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ConfirmComponent } from '../dialogs/confirm/confirm.component';
import { EffortsService } from '../services/efforts.service';
import { FilterEditorComponent } from '../components/filter-editor/filter-editor.component';
import { FilterChip } from '../models/filter-chip';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { FilterChipUtilityService } from '../services/filter-chip-utility.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UsersService } from '../services/user.service';
import { User } from '../models/user';
import { TableUIPreferences, UIPreference } from '../models/ui-preferences';
import { GroupsService } from '../services/groups.service';

@Component({
    selector: 'app-efforts-management',
    standalone: true,
    templateUrl: './efforts-management.component.html',
    styleUrls: ['./efforts-management.component.scss'],
    imports: [
        CommonModule,
        MatTableModule,
        MatExpansionModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatChipsModule,
        MatAutocompleteModule,
        MatOptionModule,
        ReactiveFormsModule,
        MatSelectModule,
        FormsModule,
        MatButtonModule,
        FilterEditorComponent,
        RouterModule,
        MatSortModule,
        MatPaginatorModule,
        MatProgressSpinnerModule
    ],
    animations: [
        trigger('fadeAnimation', [
            transition(':enter', [
                style({ opacity: '0' }),
                animate('0.2s 300ms ease-in', style({ opacity: '1' }))
            ]),
            transition(':leave', [
                style({ opacity: '1' }),
                animate('0.2s ease-out', style({ opacity: '0' }))
            ])
        ]),
        trigger('fadeExpand', [
            state('false', style({ display: 'none' })),
            state('true', style({ display: '*' })),
            transition('* => true', [
                animate('500ms ease-in', keyframes([style({ display: '*', offset: 0 })])),
                query('.effort-editor', [
                    style({
                        opacity: 0,
                        height: '0px',
                        transform: 'translateY(-10px)'
                    }),
                    animate('500ms ease-in', style({ opacity: 1, height: '*' }))
                ])
            ]),
            transition('true => false', [
                query('.effort-editor', [
                    style({
                        opacity: 1,
                        height: '*'
                    }),

                    animate('500ms ease-out', style({ opacity: 0, height: '0px', transform: 'translateY(-10px)' }))
                ])
            ]),
            // transition('true => false', [
            //     animate('500ms ease-out')
            // ])
        ]),
        trigger('rowAnimation', [
            state('false', style({ display: 'none' })),
            transition('0 => 1', [
                animate('500ms ease-in', keyframes([style({ display: '*', opacity: 1, height: '*', offset: 0 })]))
            ]),
            transition('1 => 0', [
                animate('500ms ease-out', keyframes([style({ display: 'none', offset: 1.0 })]))
            ])
        ])
    ]
})
export class EffortsManagementComponent {
    //@Input() efforts: Effort[] = [{ id: 1, title: 'Bonus round', tags: ["awesome"], users: [] }];
    @Input() allTags: string[] = [];
    @Input() allMembers: string[] = [];
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    efforts!: Effort[];
    filterControl = new FormControl('');
    filters: FilterChip[] = [];
    selectedFilters: Subject<FilterChip[]> = new BehaviorSubject<FilterChip[]>([]);
    _loading = new BehaviorSubject<boolean>(false);
    isLoading = this._loading.pipe(debounceTime(environment.spinnerDelay));
    readonly effortsService = inject(EffortsService);
    readonly deptsService = inject(GroupsService);
    readonly http = inject(HttpClient);
    readonly activatedRoute = inject(ActivatedRoute);
    readonly router = inject(Router);
    readonly data = this.activatedRoute.data;
    readonly dialog = inject(MatDialog);
    readonly filterUtility = inject(FilterChipUtilityService);
    readonly userService = inject(UsersService);
    readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    readonly titleFilter = new FormControl('');
    readonly departmentFilter = new FormControl('');
    readonly tagFilter = new FormControl('');
    readonly expandedEffort = signal<number | null>(null);
    currentTag = '';
    readonly dataSource = new MatTableDataSource();
    private _dataChangedSubject = new Subject<FilterChip[]>();
    private dataChanged$ = this._dataChangedSubject.asObservable();
    subscription: any;
    totalEfforts: number = 0;
    currentUser?: User;
    uiPreference?: UIPreference;
    pageSubscr?: Subscription;
    canAdd: boolean = false;
    pageSize = 10;
    readonly chipHelpers = {
        "title": "Filters by Effort's title",
        "key": "Filters by Effort's key",
        "dept": "Filters by Effort's owning department",
        "archived": "Filters by whether an Effort has been marked as archived",
    };
    readonly chipPossVals: { [key: string]: string[] | ((v: string) => Promise<string[] | string[][]>) } = {
        'dept': (value: string) =>
            firstValueFrom(this.deptsService.filterGroups([{ chip: null, value }]).pipe(map((o => o.groups.map(u => [u.key, `${u.name} (${u.key})`]))))),
    }

    constructor() {
        this.userService.getCurrentUser().subscribe(user => {
            this.currentUser = user;
            this.uiPreference = user.userUIPreference?.data ? JSON.parse(user.userUIPreference.data) : new UIPreference();
            this.pageSize = this.uiPreference?.efforts?.pageSize ?? 10;
            // console.log("UI Preferences", uiPreference, user.userUIPreference);
            const roles = user.userRoles.map(ur => ur.role.roleName.toLowerCase());
            this.canAdd = roles.includes("admin") || (roles.includes("fullaccess") && !!user.groupMemberships.length);
            console.log(roles, user.groupMemberships);
        });
    }

    ngAfterViewInit() {
        combineLatest([
            this.activatedRoute.params.pipe(filter(a => !!a)),
            this.activatedRoute.queryParams.pipe(filter(a => !!a)),
            this.activatedRoute.data.pipe(filter(a => !!a))
        ]).subscribe(this.handleParams.bind(this));
        combineLatest([
            this.dataChanged$.pipe(debounceTime(100)),
            this.sort.sortChange.pipe(startWith({ active: this.sort.active, direction: this.sort.direction })),
            this.paginator.page.pipe(startWith({ pageSize: this.paginator.pageSize, pageIndex: this.paginator.pageIndex })),
        ]).subscribe(([filters, sort, page]) => {
            console.log("paaaging", page.pageSize, page.pageIndex);
            this._loading.next(true);
            if (this.subscription) this.subscription.unsubscribe();
            this.subscription = this.effortsService.filterEfforts(filters, page.pageSize, page.pageIndex, sort.active, sort.direction).subscribe(resp => {
                console.log(resp);
                this._loading.next(false)
                this.filters = filters;
                this.dataSource.data = resp.efforts;
                this.totalEfforts = resp.total;
            });
        });
        this.pageSubscr = this.paginator.page
            .pipe(map(ev => ev.pageSize), distinct())
            .subscribe(size => {
                if (!this.uiPreference) return;

                this.uiPreference!.efforts ??= new TableUIPreferences();
                this.uiPreference.efforts.pageSize = size;
                this.userService.savePreference(this.uiPreference!, this.currentUser!.id).subscribe(uipref => {
                    console.info("Saved pageSize preference.");
                });
            });
    }

    ngOnDestroy() {
        this.pageSubscr?.unsubscribe();
    }

    handleParams([params, p, data]: [Params, Params, Data]) {
        const filters = [];
        console.log(params, p, data);
        if (p["filter.key"]) filters.push({ chip: 'key', value: p["filter.key"] });
        if (p["filter.dept"]) filters.push({ chip: 'dept', value: p["filter.dept"] });
        if (p["filter.dept.key"]) filters.push({ chip: 'dept.key', value: p["filter.dept.key"] });
        if (p["filter.title"]) filters.push({ chip: 'title', value: p["filter.title"] });
        if (p["filter.keywords"]) filters.push({ chip: null, value: p["filter.keywords"] });
        if (p["filter.archived"]) filters.push(<FilterChip>{ chip: 'archived', isFlag: true, value: '1' });
        this.paginator.firstPage();
        this.filters = filters;
        this.selectedFilters.next(filters);
        this.efforts = (<{ efforts: Effort[] }>data).efforts;
        this._dataChangedSubject.next(filters);
    }

    canModify(effort: Effort) {
        if (!this.currentUser) return false;
        const isAdmin = this.currentUser!.userRoles.find(ur => "admin" == ur?.role?.roleName?.toLowerCase()) ?? false;
        if (isAdmin) return true;

        const isDeptAdmin = this.currentUser!.userRoles.find(ur => "fullaccess" == ur?.role?.roleName?.toLowerCase()) ?? false;
        return isDeptAdmin && this.currentUser.groupMemberships.find(gm => gm.groupId == effort.principalGroupId);
    }

    toggleExpand(effortId: number) {
        this.expandedEffort.set(this.expandedEffort() === effortId ? null : effortId);
    }

    removeTag(effort: Effort, tag: string) {
        effort.tags = effort.tags.filter(t => t !== tag);
    }

    addTag(effort: Effort, event: any) {
        const value = event.option.viewValue.trim();
        if (value && !effort.tags.includes(value)) {
            effort.tags.push(value);
        }
    }

    removeMember(effort: Effort, user: string) {
        effort.users = effort.users.filter(m => m.user.hawkId !== user);
    }

    async openEditor(effort: Effort | null, isNew: boolean): Promise<boolean> {
        this.dialog.open(EffortEditorComponent, {
            width: '600px',
            data: { isNew, effort }
        }).afterClosed().subscribe(async (result: { effort: Effort, isNew: boolean }) => {
            if (result && !result.isNew && result.effort) {
                // result.effort.projects = <any>undefined;
                console.info(this.openEditor.name, 'Updated Effort:', result.effort);
                this.effortsService.updateEffort(result.effort);
            }

            if (result && result.isNew && result.effort) {
                this.effortsService.addEffort(result.effort);
            }
        });
        return false;
    }

    async openEffortDetails(effort: Effort) {
        console.info(this.openEffortDetails.name, effort.key, "effort key")
        this.router.navigate(["/projects"], {
            queryParams: { 'filter.effort': effort.key ?? effort.id }
        });
    }

    async deleteEffort(effort: Effort) {
        console.info(this.deleteEffort, effort);
        const message = `Are you sure you want to delete '${effort.title}'?`;
        this.dialog.open(ConfirmComponent, {
            width: '400px',
            data: { title: "Are you sure?", message }
        })
            .afterClosed().subscribe(async result => {
                if (result) {
                    this.effortsService.deleteEffort(effort);
                }
            });
    }

    filterChange(filters: FilterChip[]): void {
        console.log("Filter changed", filters);
        this.filterUtility.updateUrlWithFilters(filters, '/efforts');
        this._loading.next(true);
        this._dataChangedSubject.next(filters);
    }
}
